import React, { useCallback, useEffect, useState } from "react";
import { Snowflake, Ghost, PartyPopper, Flower2, Sun, X, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

// Dev → THEMES — one seasonal theme at a time, applied to the whole public site
const THEMES = [
  { id: "christmas", icon: Snowflake, swatch: ["#B3233A", "#1F6B3A", "#F2C230"] },
  { id: "halloween", icon: Ghost, swatch: ["#D9551E", "#7B4BA3", "#2A1F2E"] },
  { id: "apokries", icon: PartyPopper, swatch: ["#C42B9C", "#14A496", "#F2C230"] },
  { id: "easter", icon: Flower2, swatch: ["#B76BA3", "#7CB350", "#F2B8D0"] },
  { id: "summer", icon: Sun, swatch: ["#0E8FA8", "#E8902E", "#F5C33B"] },
];

export const ThemePanel = () => {
  const { t } = useLang();
  const th = t.themes;
  const [active, setActive] = useState(null);
  const [splash, setSplash] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/theme").then((r) => { setActive(r.data?.active || "none"); setSplash(!!r.data?.splash); }).catch(() => setActive("none"));
  }, []);
  useEffect(load, [load]);

  const set = async (id) => {
    setSaving(true);
    try {
      const next = active === id ? "none" : id;
      await api.put("/admin/theme", { active: next });
      setActive(next);
      toast.success(next === "none" ? th.off_toast : th.on_toast);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const toggleSplash = async () => {
    setSaving(true);
    try {
      const next = !splash;
      await api.put("/admin/theme", { active, splash: next });
      setSplash(next);
      toast.success(next ? th.splash_on : th.splash_off);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (active === null) return null;
  return (
    <div className="space-y-4" data-testid="theme-panel">
      <p className="text-sm text-brand-ink/60 bg-brand-soft/40 border border-brand/10 rounded-2xl px-4 py-3">{th.hint}</p>
      {/* Christmas entry splash toggle */}
      <button
        onClick={toggleSplash}
        disabled={saving}
        className={`w-full flex items-center justify-between gap-4 rounded-2xl border-2 px-5 py-4 transition-all no-a11y-underline ${splash ? "border-brand bg-brand-soft/40" : "border-brand/10 bg-white hover:border-brand/40"}`}
        data-testid="theme-splash-toggle"
      >
        <span className="text-left">
          <span className="block font-bold text-brand-ink">{th.splash_label}</span>
          <span className="block text-xs text-brand-ink/50 mt-0.5">{th.splash_hint}</span>
        </span>
        <span className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${splash ? "bg-brand" : "bg-stone-alt"}`}>
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${splash ? "left-6" : "left-1"}`} />
        </span>
      </button>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {THEMES.map((tm) => {
          const Icon = tm.icon;
          const on = active === tm.id;
          return (
            <button
              key={tm.id}
              onClick={() => set(tm.id)}
              disabled={saving}
              className={`relative text-left rounded-3xl border-2 p-5 transition-all no-a11y-underline ${
                on ? "border-brand bg-brand-soft/40 shadow-[0_14px_40px_rgba(134,77,99,0.18)]" : "border-brand/10 bg-white hover:border-brand/40"
              }`}
              data-testid={`theme-${tm.id}`}
            >
              {on && (
                <span className="absolute top-3 right-3 grid place-items-center h-7 w-7 rounded-full bg-brand text-brand-cream" data-testid={`theme-active-${tm.id}`}>
                  <Check className="h-4 w-4" />
                </span>
              )}
              <span className="grid place-items-center h-14 w-14 rounded-2xl mb-3" style={{ background: `linear-gradient(135deg, ${tm.swatch[0]}, ${tm.swatch[1]})` }}>
                <Icon className="h-7 w-7 text-white" />
              </span>
              <span className="block font-heading text-xl font-bold text-brand-ink">{th.names[tm.id]}</span>
              <span className="block text-xs text-brand-ink/50 mt-1">{th.desc[tm.id]}</span>
              <span className="mt-3 flex gap-1.5">
                {tm.swatch.map((c) => (
                  <span key={c} className="h-4 w-4 rounded-full border border-black/10" style={{ background: c }} />
                ))}
              </span>
              <span className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${on ? "bg-brand text-brand-cream" : "bg-stone-alt/60 text-brand-ink/60"}`}>
                {on ? <X className="h-3.5 w-3.5" /> : null}
                {on ? th.deactivate : th.activate}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemePanel;
